import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';

import type { AvatarAnimation, ChatbotAvatarProps } from './avatarTypes';
import styles from './ChatbotAvatar.module.css';

const BASE_AVATAR_SIZE = 180;
const ONE_SHOT_DURATION_MS: Record<AvatarAnimation, number> = {
  blink: 260,
  bounce: 520,
  slowblink: 820,
  think: 1900,
  widen: 360,
};

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

function getAnimationClass(animation: AvatarAnimation) {
  return styles[animation] ?? '';
}

export const ChatbotAvatar = forwardRef<HTMLButtonElement, ChatbotAvatarProps>(
  function ChatbotAvatar(
    {
      animation = null,
      className,
      decorative = false,
      idleGlow = false,
      onClick,
      onKeyDown,
      size = 40,
      state = 'neutral',
      tilt = false,
      'aria-label': ariaLabel = 'Open AI study assistant',
    },
    ref,
  ) {
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const avatarRef = useRef<HTMLDivElement | null>(null);
    const animationTimeoutRef = useRef<number | null>(null);
    const scale = size / BASE_AVATAR_SIZE;

    useImperativeHandle(ref, () => buttonRef.current as HTMLButtonElement);

    useEffect(() => {
      const avatar = avatarRef.current;

      if (!avatar || !animation) {
        return;
      }

      const animationClass = getAnimationClass(animation);

      if (!animationClass) {
        return;
      }

      if (animationTimeoutRef.current !== null) {
        window.clearTimeout(animationTimeoutRef.current);
      }

      avatar.classList.remove(animationClass);
      void avatar.offsetWidth;
      avatar.classList.add(animationClass);

      animationTimeoutRef.current = window.setTimeout(() => {
        avatar.classList.remove(animationClass);
        animationTimeoutRef.current = null;
      }, ONE_SHOT_DURATION_MS[animation] + 120);

      return () => {
        if (animationTimeoutRef.current !== null) {
          window.clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }

        avatar.classList.remove(animationClass);
      };
    }, [animation]);

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
      onKeyDown?.(event);

      if (event.defaultPrevented || !onClick) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.currentTarget.click();
      }
    };

    return (
      <button
        aria-hidden={decorative ? 'true' : undefined}
        aria-label={decorative ? undefined : ariaLabel}
        className={classNames(styles.avatarFrame, className)}
        data-decorative={decorative ? 'true' : 'false'}
        onClick={decorative ? undefined : onClick}
        onKeyDown={decorative ? undefined : handleKeyDown}
        ref={buttonRef}
        style={{
          height: `${size}px`,
          width: `${size}px`,
        }}
        tabIndex={decorative ? -1 : undefined}
        type="button"
      >
        <span
          aria-hidden="true"
          className={classNames(
            styles.crtAvatar,
            idleGlow && styles.idle,
            tilt && styles.tilt,
          )}
          data-state={state}
          data-testid="crt-avatar"
          ref={avatarRef}
          style={{
            '--avatar-scale': String(scale),
          } as CSSProperties}
        >
          <span className={styles.crtScreen}>
            <span className={styles.crtEyes}>
              <span className={styles.crtEye} />
              <span className={styles.crtEye} />
            </span>
          </span>
        </span>
      </button>
    );
  },
);

ChatbotAvatar.displayName = 'ChatbotAvatar';
